

import ChecklistsTab from '@components/ProjectOverview/Checklist/Checklist';
import InspectionsTab from '@components/ProjectOverview/Inspections/Inspections';
import { ButtonContainer, InfoBoxContainer, ProjectAvatar, ProjectContainer, ProjectHeaderContainer, ProjectId, ProjectInfoContainer, ProjectTitle, ProjectTitleContainer, ProjectTitleWrapper, TabsContainer } from '@components/ProjectOverview/Overview/Overview.styled';
import { OverviewTab } from '@components/ProjectOverview/Overview/Overview.tsx';
import ProjectUploadModal from '@components/Projects/UploadModal';
import InfoBox from '@components/Shared/InfoBox';
import Header from '@components/Shared/Layout/Header';
import { ROUTE_PATHS, type RoutePaths } from '@constants/routes';
import { useLocalization } from '@lib/localization';
import { OpenInBrowser } from '@mui/icons-material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { Alert, Box, Button, Chip, CircularProgress, Tab, Tabs, Typography, useMediaQuery, useTheme } from '@mui/material';
import { type IInspection, useLazyGetInspectionsQuery } from '@store/api/inspectionEndpoints';
import { thumbnailCache, useGetProjectQuery, useLazyGetProjectThumbnailQuery, useUpdateProjectMutation } from '@store/api/projectEndpoints';
import { useLoading } from '@store/hooks';
import { useEffect, useId, useState } from 'react';
import { CodeBranchIcon, CubeIcon, UploadIcon } from 'react-line-awesome';
import { generatePath, useMatch, useNavigate, useParams } from 'react-router-dom';

interface DashBoardTabsProps {
  value: number
  onChange: (event: React.SyntheticEvent, newValue: number) => void
}

const DashboardTabs: React.FC<DashBoardTabsProps> = ({ value, onChange }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useLocalization();

  return (
    <TabsContainer>
      <Tabs
        value={value}
        onChange={onChange}
        indicatorColor="primary"
        textColor="primary"
        variant={isMobile ? 'fullWidth' : 'standard'}
      >
        <Tab label={t('Common_Overview')} style={{ minWidth: isMobile ? 'auto' : '200px' }} />
        <Tab label={t('Common_Inspections')} style={{ minWidth: isMobile ? 'auto' : '200px' }} />
        <Tab label={t('Common_Checklists')} style={{ minWidth: isMobile ? 'auto' : '200px' }} />
      </Tabs>
    </TabsContainer>
  );
};

const ProjectOverviewPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { t } = useLocalization();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [modalOpen, setModalOpen] = useState(false);

  const [updateProject, { isLoading: isUpdateProjectLoading }] = useUpdateProjectMutation();

  const [getProjectThumbnail, { isLoading: isProjectThumbnailLoading }] = useLazyGetProjectThumbnailQuery();
  const [tabValue, setTabValue] = useState(0);
  const [thumbnailUrl, setThumbnailUrl] = useState<string | undefined>();
  const { show, hide } = useLoading();
  const { projectId } = useParams();

  const { data: project, isLoading } = useGetProjectQuery(projectId!);

  const [inspections, setInspections] = useState<IInspection[]>([]);
  const [getInspections, { isLoading: isInspectionListLoading }] = useLazyGetInspectionsQuery();

  const componentId = useId();

  useEffect(() => {
    const isProjectNotReady = !project || isLoading;
    const isNotReady = isProjectNotReady || isProjectThumbnailLoading || isInspectionListLoading
      ; (isNotReady ? show : hide)(componentId);
  }, [componentId, hide, isInspectionListLoading, isLoading, isProjectThumbnailLoading, project, show]);

  const isOverviewTab = useMatch(ROUTE_PATHS.PROJECT);
  const isInspectionsTab = useMatch(ROUTE_PATHS.PROJECT_INSPECTIONS);
  const isChecklistsTab = useMatch(ROUTE_PATHS.PROJECT_CHECKLISTS);
  const isInspectionDetail = useMatch(ROUTE_PATHS.PROJECT_INSPECTION_POI_STEP);

  useEffect(() => {
    if (isOverviewTab) {
      setTabValue(0);
    } else if (isInspectionsTab || isInspectionDetail) {
      setTabValue(1);
    } else if (isChecklistsTab) {
      setTabValue(2);
    } else {
      setTabValue(0);
    }
  }, [isChecklistsTab, isInspectionDetail, isInspectionsTab, isOverviewTab]);

  useEffect(() => {
    const initInspectionList = async (): Promise<void> => {
      if (!projectId || !project) {
        setInspections([]);
        setThumbnailUrl('');
        return;
      }

      try {
        const thumbnailUrl = thumbnailCache.get(projectId);
        if (thumbnailUrl != undefined) setThumbnailUrl(thumbnailUrl);

        const [inspectionsResponse, thumbnailResponse] = await Promise.all([
          getInspections({ projectId, include: ['notes', 'pointOfInterests'] }),
          thumbnailUrl === undefined ? getProjectThumbnail(project.projectInfo.id) : undefined,
        ]);

        setInspections(inspectionsResponse.data ?? []);
        if (thumbnailResponse) setThumbnailUrl(thumbnailResponse.data);
      } catch (error) {
        console.error('Failed to load project data:', error);
        setInspections([]);
        setThumbnailUrl('');
      }
    };

    initInspectionList();
  }, [projectId, project, getInspections, getProjectThumbnail]);

  const openInViewer = () => {
    const projectRoute = generatePath(ROUTE_PATHS.VIEWER_PROJECT, { projectId: projectId! });
    const newWindow = window.open(projectRoute, '_blank');

    if (!newWindow) navigate(projectRoute);
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    let routePath: RoutePaths;

    switch (newValue) {
      case 0:
        routePath = ROUTE_PATHS.PROJECT;
        break;
      case 1:
        routePath = ROUTE_PATHS.PROJECT_INSPECTIONS;
        break;
      case 2:
        routePath = ROUTE_PATHS.PROJECT_CHECKLISTS;
        break;
      default:
        routePath = ROUTE_PATHS.PROJECT;
        break;
    }

    const route = generatePath(routePath, { projectId: projectId! });

    navigate(route);
  };

  return project ? (
    <>
      <Header />
      {
        isUpdateProjectLoading && <Alert
          severity="warning"
          icon={<CircularProgress size={20} color="warning" />}
          sx={{ width: '100%', justifyContent: 'center', marginTop: '0 !important', marginBottom: '16px !important' }}
        >
          <Typography variant="body2" fontWeight={500}>
            {t('Project_Upload_InProgress')}
          </Typography>
        </Alert>
      }

      <ProjectContainer isMobile={isMobile}>
        <ProjectAvatar src={thumbnailUrl} variant="square" isMobile={isMobile}>
          <CubeIcon size='5x' style={{ color: 'white' }} />
        </ProjectAvatar>
        <ProjectInfoContainer isMobile={isMobile}>
          <ProjectHeaderContainer isMobile={isMobile}>
            <ProjectTitleContainer isMobile={isMobile}>
              <ProjectTitleWrapper isMobile={isMobile}>
                <ProjectTitle variant="h6">{project.projectInfo.name}</ProjectTitle>
                {inspections.length > 0 && (
                  <Chip
                    label={t('Inspections_Count', { count: inspections.length })}
                    size="small"
                    sx={{ marginLeft: isMobile ? 0 : 1 }}
                  />
                )}
              </ProjectTitleWrapper>
              <ProjectId variant="body2">{project.projectInfo.id}</ProjectId>
            </ProjectTitleContainer>
            <ButtonContainer isMobile={isMobile}>
              <Button
                variant="outlined"
                startIcon={<UploadIcon />}
                size={isMobile ? 'small' : 'medium'}
                fullWidth={isMobile}
                onClick={() => { setModalOpen(true); }}
              >
                {t('Project_Manual_Update')}
              </Button>
              <Button
                variant="contained"
                onClick={openInViewer}
                startIcon={<OpenInBrowser />}
                size={isMobile ? 'small' : 'medium'}
                fullWidth={isMobile}
              >
                {t('Project_View')}
              </Button>
            </ButtonContainer>
          </ProjectHeaderContainer>

          <InfoBoxContainer isMobile={isMobile}>
            <InfoBox
              icon={<CalendarTodayIcon sx={{ fontSize: 16, color: '#aaa' }} />}
              text={t('Common_DateCreated')}
              value={new Date(project.projectInfo.dateCreated).toAgoString()}
            />
            <InfoBox
              icon={<CalendarTodayIcon sx={{ fontSize: 16, color: '#aaa' }} />}
              text={t('Common_DateUpdated')}
              value={new Date(project.projectInfo.dateModified).toAgoString()}
            />
            <InfoBox
              icon={<CodeBranchIcon style={{ fontSize: 16, color: '#aaa' }} />}
              text={t('Common_Version')}
              value={project.projectInfo.version}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', ml: isMobile ? 0 : 'auto' }}></Box>
          </InfoBoxContainer>
        </ProjectInfoContainer>
      </ProjectContainer>
      <DashboardTabs value={tabValue} onChange={handleTabChange} />
      {tabValue === 0 && <OverviewTab inspections={inspections} />}
      {tabValue === 1 && <InspectionsTab projectId={projectId!} inspections={inspections} />}
      {tabValue === 2 && <ChecklistsTab projectId={projectId!} inspections={inspections} />}

      <ProjectUploadModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        existingInspections={inspections}
        uploadProject={async (formData: FormData) => { await updateProject({ id: projectId!, formData }); }}
      />
    </>
  ) : null;
};

export default ProjectOverviewPage;
